<!-- `overflow-x-clip` et non `overflow-x-hidden` : « hidden » sur un seul axe
     force l'autre en `auto` et fabrique un conteneur de défilement imbriqué,
     ce qui casserait le scroll de la page. `clip` se contente de couper. -->
<div class="overflow-x-clip" @pageFromRight>
<vw-header-padding-safe-area></vw-header-padding-safe-area>

<div class="px-4 pb-28">
  <div class="flex items-center gap-2 pb-4">
    <button
      type="button"
      class="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-primary-dark"
      (click)="goBack()"
      [attr.aria-label]="'common.back' | translate"
    >
      <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" aria-hidden="true">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <h2 class="text-[28px] font-semibold text-primary-dark">
      {{ 'weather.detail.title' | translate }}
    </h2>
  </div>

  @if (isLoading) {
    <vw-skeleton-loader [width]="'100%'" [height]="'140px'" [borderRadius]="'0.75rem'"></vw-skeleton-loader>
  } @else if (failed || !days.length) {
    <p class="py-10 text-center text-sm text-[#9BA6A1]">
      {{ 'weather.detail.unavailable' | translate }}
    </p>
  } @else {

  <!-- Le verdict d'abord : l'écran donnait la matière et laissait conclure,
       alors que la conclusion est justement ce que l'app sait faire. -->
  @if (verdict; as v) {
    <div
      class="flex items-start gap-3 rounded-2xl px-4 py-3.5"
      [style.background-color]="style(v.condition).bg"
    >
      <span class="text-2xl leading-none">{{ style(v.condition).emoji }}</span>
      <p class="text-[15px] font-medium leading-snug" [style.color]="style(v.condition).ink">
        {{ v.key | translate: v.params }}
      </p>
    </div>
  }

  <!-- Trois chiffres, trois questions : depuis quand est-ce sec, quand
       pleut-il, et jusqu'où monte-t-il. La hauteur réservée sur les libellés
       aligne les valeurs — sans elle, celui qui tient sur deux lignes fait
       descendre son chiffre. -->
  <div class="mt-2 grid grid-cols-3 gap-2">
    <div class="rounded-2xl bg-white px-3 py-3 shadow-sm">
      <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-[#9BA6A1]">
        {{ 'weather.detail.dry-streak' | translate }}
      </p>
      <p class="mt-1 text-xl font-semibold text-primary-dark">
        {{ outlook?.dryStreak ?? 0 }}<span class="ml-0.5 text-xs font-normal text-[#617069]">{{ 'weather.detail.days-short' | translate }}</span>
      </p>
    </div>

    <div class="rounded-2xl bg-white px-3 py-3 shadow-sm">
      <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-[#9BA6A1]">
        {{ 'weather.detail.upcoming-rain' | translate }}
      </p>
      <p class="mt-1 text-xl font-semibold text-primary-dark">
        {{ outlook?.upcomingRainMm ?? 0 }}<span class="ml-0.5 text-xs font-normal text-[#617069]">mm</span>
      </p>
      <!-- L'échéance, pas seulement le cumul : c'est elle qui dit s'il faut
           arroser ce soir ou attendre. -->
      <p class="text-[10px] leading-tight text-[#617069]">
        @if (nextRainInDays === null) {
          {{ 'weather.detail.rain-none' | translate }}
        } @else if (nextRainInDays === 0) {
          {{ 'weather.detail.rain-today' | translate }}
        } @else if (nextRainInDays === 1) {
          {{ 'weather.detail.rain-tomorrow' | translate }}
        } @else {
          {{ 'weather.detail.rain-in-days' | translate: { count: nextRainInDays } }}
        }
      </p>
    </div>

    <!-- Le pic à venir : c'est lui qu'on prépare, pas la moyenne. -->
    <div class="rounded-2xl bg-white px-3 py-3 shadow-sm">
      <p class="min-h-[26px] text-[10px] uppercase leading-tight tracking-wide text-[#9BA6A1]">
        {{ 'weather.detail.peak' | translate }}
      </p>
      @if (peakDay; as peak) {
        <p class="mt-1 text-xl font-semibold text-primary-dark">
          {{ peak.tempMax }}<span class="text-xs font-normal text-[#617069]">°</span>
        </p>
        <p class="text-[10px] leading-tight text-[#617069]">
          {{ peak.isToday ? ('weather.detail.today' | translate) : peak.weekday + ' ' + peak.dayNumber }}
        </p>
      } @else {
        <p class="mt-1 text-xl font-semibold text-[#9BA6A1]">—</p>
      }
    </div>
  </div>

  <!-- La fenêtre, jour par jour -->
  <h3 class="mt-6 text-lg font-semibold text-primary-dark">
    {{ 'weather.detail.window' | translate }}
  </h3>
  <!-- Une barre sans légende n'est pas une donnée, c'est une décoration :
       la précédente montrait la pluie au prorata du jour le plus arrosé, ce
       qui la rendait illisible une semaine sèche. -->
  <p class="pb-2 text-xs leading-snug text-[#9BA6A1]">
    {{ 'weather.detail.temp-legend' | translate }}
  </p>

  <div class="flex flex-col gap-2">
    @for (day of days; track day.date) {
      <div
        class="rounded-2xl border-2 px-3 py-2.5"
        [style.background-color]="style(day.condition).bg"
        [style.border-color]="day.isToday ? style(day.condition).ink : 'transparent'"
        [class.opacity-70]="day.isPast"
      >
        <div class="flex items-center gap-3">
          <!-- Date. Le jour même porte sa pastille pleine : le mot
               « aujourd'hui » en plus du cadre volait la place du libellé, qui
               se retrouvait tronqué en « Forte chal… ». -->
          <div class="w-10 shrink-0 text-center">
            <p class="text-[11px] uppercase leading-tight text-[#617069]">{{ day.weekday }}</p>
            @if (day.isToday) {
              <p
                class="mx-auto mt-0.5 h-6 w-6 rounded-full text-base font-semibold leading-6 text-white"
                [style.background-color]="style(day.condition).ink"
                [attr.aria-label]="'weather.detail.today' | translate"
              >
                {{ day.dayNumber }}
              </p>
            } @else {
              <p class="text-base font-semibold leading-tight" [style.color]="style(day.condition).ink">
                {{ day.dayNumber }}
              </p>
            }
          </div>

          <span class="shrink-0 text-xl">{{ style(day.condition).emoji }}</span>

          <p class="min-w-0 flex-1 truncate text-sm font-medium" [style.color]="style(day.condition).ink">
            {{ 'weather.detail.conditions.' + day.condition | translate }}
          </p>

          <!-- Pluie et vent : signalés quand ils changent quelque chose, jamais
               un « 0 mm » sur chaque ligne sèche. -->
          @if ((day.precipitation ?? 0) > 0) {
            <span class="shrink-0 text-[11px] font-medium text-[#1F4E85]">
              💧 {{ day.precipitation }} mm
            </span>
          }
          @if (day.windSpeed && day.windSpeed >= windNotable) {
            <span class="shrink-0 text-[11px] text-[#3E5250]">💨 {{ day.windSpeed }}</span>
          }
        </div>

        <!-- La barre thermique, sur une échelle COMMUNE à la fenêtre : c'est
             ce qui fait voir la chaleur monter puis retomber. -->
        <div class="mt-1.5 flex items-center gap-2">
          <span class="w-10 shrink-0 text-right text-[11px] text-[#617069]">
            {{ day.tempMin ?? '—' }}°
          </span>
          <div class="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/70">
            @if (day.tempMin !== null && day.tempMax !== null) {
              <div
                class="absolute inset-y-0 rounded-full"
                [style.left.%]="bandLeft(day)"
                [style.width.%]="bandWidth(day)"
                [style.background-color]="style(day.condition).bar"
              ></div>
            }
          </div>
          <span class="w-10 shrink-0 text-[11px] font-semibold" [style.color]="style(day.condition).ink">
            {{ day.tempMax ?? '—' }}°
          </span>
        </div>
      </div>
    }
  </div>

  <p class="mt-4 text-xs leading-relaxed text-[#9BA6A1]">
    {{ 'weather.detail.footnote' | translate }}
  </p>
  }
</div>
</div>
